import type { RxConflictHandler, RxConflictHandlerInput } from 'rxdb';

/**
 * Base shape all syncable documents must satisfy.
 * Every collection schema includes these fields.
 * Includes `_deleted` to satisfy RxDB's `WithDeleted<T>` type.
 */
export interface SyncableDocument {
  id: string;
  updated_at: string;
  is_deleted: boolean;
  last_modified_by: string;
  sync_state?: 'synced' | 'pending' | 'conflict';
  _deleted: boolean;
  [key: string]: unknown;
}

/**
 * Last-Write-Wins conflict handler with local pending state protection.
 *
 * Strategy:
 * - If local state is 'pending' (has unpushed local modifications), local state MUST
 *   be protected and preserved against incoming server pulls, UNLESS the server document
 *   has a strictly higher `updated_at` timestamp.
 * - Once local changes are successfully pushed to Supabase, the `sent$` stream patches
 *   ``.
 */
export const lwwConflictHandler: RxConflictHandler<SyncableDocument> = {
  isEqual(a, b, _context) {
    return a.updated_at === b.updated_at && a.sync_state === b.sync_state;
  },

  resolve(
    input: RxConflictHandlerInput<SyncableDocument>,
    _context: string,
  ) {
    const { newDocumentState, realMasterState } = input;

    // Regla de protección 1: Si el document local tiene cambios pendientes de enviar ('pending')
    if (newDocumentState.sync_state === 'pending') {
      // Si la nube tiene un timestamp estrictamente más reciente (edición posterior en otro dispositivo)
      if (realMasterState.updated_at > newDocumentState.updated_at) {
        console.log(`[Sync Conflict] La nube tiene una versión estrictamente más reciente para ${newDocumentState.id}. Aceptando nube.`);
        return Promise.resolve(realMasterState);
      }

      // Preservar la modificación local pendiente
      return Promise.resolve(newDocumentState);
    }

    // Regla 2: Para documentos ya sincronizados localmente ('synced')
    if (newDocumentState.updated_at > realMasterState.updated_at) {
      return Promise.resolve(newDocumentState);
    }

    // La nube gana si tiene timestamp igual o mayor
    return Promise.resolve(realMasterState);
  },
};
